<template>
    <v-col v-bind="DASHBOARD_WIDGET_COLUMNS.SMALL">
        <v-card>
            <v-toolbar color="grey-lighten-4" density="compact" flat>
                <template #title>{{ displayTitle }}</template>

                <template #append>
                    <v-menu v-if="hasModulePermission">
                        <template #activator="{ props: activatorProps }">
                            <v-btn v-bind="activatorProps" icon="mdi-dots-vertical" size="small" variant="text" />
                        </template>

                        <v-list density="compact">
                            <v-list-item :title="t('common.dashboard.buttons.goToModule')" @click="redirect" />
                        </v-list>
                    </v-menu>
                </template>
            </v-toolbar>

            <v-divider />

            <v-card-text class="text-center">
                <div class="text-h4">{{ count }}</div>
            </v-card-text>
        </v-card>
    </v-col>
</template>

<script setup lang="ts">
/*
Organization / ordering applied:
1. Imports
2. Props / Emits
3. Constants
4. Stores & top-level services
5. Reactive state (refs/reactive)
6. Validation rules & setup
7. Computed
8. Handlers / methods
9. Helpers / lifecycle
*/

// 1. Imports
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { errorHandler } from '@/composables/errorHandler'
import type { DashboardPermission, DashboardWidget } from '@/stores/dashboard'
import {
    DASHBOARD_WIDGET_COLUMNS,
    getDashboardWidgetDisplayTitle,
    getWidgetBodyCount,
    resolveDashboardRedirectRouteName
} from '@/views/Dashboard/dashboardUtils'

// 2. Props / Emits
const props = withDefaults(
    defineProps<{
        widget: DashboardWidget
        permissions?: DashboardPermission[]
    }>(),
    { permissions: () => [] }
)

// 3. Constants
// (none)

// 4. Stores & top-level services
const router = useRouter()
const { t } = useI18n()

// 5. Reactive state (refs/reactive)
// (none)

// 6. Validation rules & setup
// (none)

// 7. Computed
const hasModulePermission = computed(() =>
    (props.permissions ?? []).some((permission) => permission?.name === props.widget?.redirectPermission)
)

const displayTitle = computed(() => getDashboardWidgetDisplayTitle(props.widget))
const count = computed(() => getWidgetBodyCount(props.widget))

// 8. Handlers / methods
async function redirect() {
    const destination = resolveDashboardRedirectRouteName(props.widget?.redirectDestination)
    if (destination == null || destination === '') return

    try {
        await router.push({ name: destination })
    } catch (error) {
        errorHandler(error)
    }
}

// 9. Helpers / lifecycle
// (none)
</script>
